<script setup lang="ts">
    import { useUserStore } from '@/stores/user';
    import { useI18n } from 'vue-i18n';
    import { useDisplay, useTheme } from 'vuetify';
    import { useNumberFormatter } from '@/composables/useNumberFormatter';
    import { BetStatus, EventFeedType, EventPeriod, Jollywhere, MarketGroup } from '@/constants';
    import { betService } from '@/services';
    import { usePageOptions } from '@/composables/usePageOptions';
    import { BetItem } from '@/models/bet';
    import { useBetsGridHeaders } from '@/pages/core/grid/headersBetsGrid';
    import { useCornersGridHeaders } from '@/pages/core/grid/headersCornersGrid';
    import { useBetsHeaders } from '@/pages/core/grid/headersBets';
    import { InternalDataTableHeader } from 'vuetify/lib/components/VDataTable/types.js';
    import FormDialog from '@/components/FormDialog.vue';
    import { GameGridItem } from '@/models/gameGrid';
    import { dateFormatInUserTimeZone, formatOdds, getCountryFlagUrl, getTeamLogoUrl } from '@/helpers';
    import { useBetStore } from '@/stores/bet';
    import { betStatusDictionary } from '@/stores';

    const isShowGameGrid = defineModel<boolean>({ default: false });

    const { gameGridItem } = defineProps<{
        gameGridItem: GameGridItem | undefined;
    }>();

    watch(isShowGameGrid, (val: boolean) => {
        if (val) {
            loadData();
        }
    });

    const { t } = useI18n();
    const { formatNumber } = useNumberFormatter();
    const tab = ref(0);
    const loading = ref(false);

    let positionBets = ref(0);
    let positionCorners = ref(0);
    const position = computed(() => positionBets.value + positionCorners.value);

    const options = usePageOptions({ sortBy: [{ key: 'created_at', order: 'desc' }] });

    const balances = ref([]);
    const cornersBalances = ref([]);
    const listDisabled = ref(true);
    const listCornersDisabled = ref(true);

    const userStore = useUserStore();
    const betStore = useBetStore();
    const theme = useTheme();
    const display = useDisplay();

    let listBetsGrid = computed({
        get() {
            return balances.value;
        },
        set(value: BetItem[]) {
            const data: any = [];
            for (let i = 0; i <= 10; i++) {
                data.push({});
                for (let j = 0; j <= 10; j++) {
                    if (j === 0) {
                        data[i]['goal'] = i;
                    }
                    const k = 'balance_' + j;
                    let totalBalance = 0;

                    value.forEach((bet: BetItem) => {
                        if (bet.statusId === BetStatus.Rejected || bet.statusId === BetStatus.Abnormal || bet.statusId === BetStatus.Void || bet.statusId === BetStatus.Refunded) {
                            console.log(`Bet #${bet.id} position calculation is skipped due to status ${betStatusDictionary.nameById(bet.statusId)}`);
                            return;
                        }

                        const result = bet.calculateExpectedResult(i, j);
                        if (result === null) return; // if live event does not have a score

                        totalBalance += Number(result.balance);
                    });

                    data[i][k] = totalBalance;

                    if (gameGridItem!.scoreHome === i && gameGridItem!.scoreAway === j) {
                        positionBets.value = totalBalance;
                    }
                }
            }

            balances.value = data;
        },
    });

    let listCornersGrid = computed({
        get() {
            return cornersBalances.value;
        },
        set(value: BetItem[]) {
            const data: any = [];
            for (let i = 0; i <= 10; i++) {
                data.push({});
                for (let j = 0; j <= 10; j++) {
                    if (j === 0) {
                        data[i]['cornersHome'] = i + (gameGridItem!.cornersHome ?? 0);
                    }
                    const k = 'balance_' + j;
                    const cornersHome = i + (gameGridItem!.cornersHome || 0);
                    const cornersAway = j + (gameGridItem!.cornersAway || 0);
                    data[i][j] = {
                        cornersSum: cornersHome + cornersAway,
                    };
                    let totalBalance = 0;

                    value.forEach((bet: BetItem) => {
                        if (bet.statusId === BetStatus.Rejected || bet.statusId === BetStatus.Abnormal || bet.statusId === BetStatus.Void || bet.statusId === BetStatus.Refunded) {
                            console.log(`Bet #${bet.id} position calculation is skipped due to status ${betStatusDictionary.nameById(bet.statusId)}`);
                            return;
                        }

                        const result = bet.calculateExpectedResult(cornersHome, cornersAway);
                        if (result === null) return; // if live event does not have a score

                        totalBalance += Number(result.balance);
                    });
                    data[i][k] = totalBalance;

                    if (gameGridItem!.cornersHome === cornersHome && gameGridItem!.cornersAway === cornersAway) {
                        positionCorners.value = totalBalance;
                    }
                }
            }
            cornersBalances.value = data;
        },
    });

    const stats = computed(() => betStore.stats);

    let listBets = computed(() => betStore.items);

    function closeGrid() {
        isShowGameGrid.value = false;
        balances.value = [];
        cornersBalances.value = [];

        betStore.items = [];

        positionBets.value = 0;
        positionCorners.value = 0;
    }

    function loadData() {
        loading.value = true;
        const params = {
            options: options.value,
            filters: {
                ...betStore.filters,
                period_type_id: gameGridItem!.periodTypeId,
                custom_event_id: gameGridItem!.customEventId,
            },
            requiredDictionaries: [],
            requiredData: ['Stats'],
            updateUserData: false,
        };

        betService.load(params).then((response) => {
            loading.value = false;
            if (response.data.success !== false) {
                let rows = betStore.items.filter((bet) => bet.marketGroup === MarketGroup.Regular);
                listBetsGrid.value = rows;
                listDisabled.value = rows.length === 0;

                rows = betStore.items.filter((bet) => bet.marketGroup === MarketGroup.Corners);
                listCornersGrid.value = rows;
                listCornersDisabled.value = rows.length === 0;

                tab.value = Number(listDisabled.value);
            }
        });
    }

    function goalClass(goal: number) {
        return goal === Number(gameGridItem!.scoreHome) ? 'bold greyBg text-center br' : goal <= Number(gameGridItem!.scoreHome) ? 'greyBg text-center br' : 'text-center br';
    }

    function cornersClass(corners: number) {
        return corners === Number(gameGridItem!.cornersHome) ? 'bold greyBg text-center br' : corners <= Number(gameGridItem!.cornersHome) ? 'greyBg text-center br' : 'text-center br';
    }

    function headClass(head: InternalDataTableHeader) {
        return Number(head.title) <= Number(gameGridItem!.scoreAway) ? 'text-center greyBg' : 'text-center';
    }

    function cornersHeadClass(head: InternalDataTableHeader) {
        const start = gameGridItem!.cornersAway ?? 0;
        return Number(head.title) + Number(start) <= Number(start) ? 'text-center greyBg' : 'text-center';
    }

    function expected(bet: BetItem) {
        let balance;

        let balanceCalculated;
        if (bet.marketGroup === MarketGroup.Regular) {
            balanceCalculated = bet.calculateExpectedResult(Number(gameGridItem!.scoreHome), Number(gameGridItem!.scoreAway));
        } else {
            balanceCalculated = bet.calculateExpectedResult(Number(gameGridItem!.cornersHome), Number(gameGridItem!.cornersAway));
        }

        if (balanceCalculated) {
            balance = balanceCalculated['balance'];
        } else {
            balance = 0; //todo check this else
        }

        let cls = balance == 0 ? 'yellowText' : Number(balance) > 0 ? 'greenText' : 'redText';

        return '<span class="' + cls + '">' + formatNumber(balance, 'money') + '</span>';
    }

    onMounted(() => {
        console.log('Component app-grid mounted.');
    });

    const headersBetGrid = useBetsGridHeaders();

    const headersCorners = useCornersGridHeaders();

    const headersBets = useBetsHeaders();

    function cornersHeaders(head: InternalDataTableHeader) {
        if (head.value === 'goal') {
            return head.title;
        }

        const start = gameGridItem!.cornersAway !== null ? gameGridItem!.cornersAway : 0;

        return Number(head.title) + Number(start);
    }
</script>

<template>
    <FormDialog v-model="isShowGameGrid" :onSubmit="() => true" :width="870" @cancelled="closeGrid" :isActionDisabled="true" :isShowActionButtons="false">
        <template v-slot:title="{ cancel }">
            <v-card-title
                class="pt-2 pb-1 d-flex justify-space-between align-center"
                :class="[display.width.value <= 667 ? 'px-0' : 'pl-3 pr-3', display.width.value > 810 ? 'title-fixed-height' : '']"
            >
                <v-container fluid class="pa-0">
                    <v-row class="pa-0 ma-0 d-flex align-center justify-center">
                        <!--                    <v-col class="text-left lightGreyText" cols="5" xs="4" sm="4" md="5" lg="5" :class="display.width.value <= 667 ? 'py-0' : ''">-->
                        <span v-if="gameGridItem!.periodTypeId === EventPeriod.FullTime" :class="display.width.value <= 667 ? 'font14' : 'font14'">{{ t('gameGrid.marketFT') }}</span>
                        <span v-else :class="display.width.value <= 667 ? 'font14' : 'font14'">{{ t('gameGrid.market1H') }}</span>

                        <v-chip v-if="gameGridItem && gameGridItem.eventFeedType === EventFeedType.Live" color="orange" label size="small" class="ml-2 live-chip" variant="outlined">
                            <span v-if="gameGridItem.time">LIVE&nbsp;{{ gameGridItem.time }}</span>
                            <span v-else class="time bold font10">LIVE&nbsp;{{ t('feed.scoreNotConfirmed') }}</span>
                        </v-chip>

                        <v-spacer></v-spacer>

                        <span v-if="betStore.items.length > 0">
                            <span v-if="display.width.value > 810" class="stats-label pl-5 pr-1" :class="display.width.value <= 667 ? 'font12' : 'font14'">{{ t('bet.bets') }}</span>
                            <v-chip v-if="display.width.value > 810" variant="flat" color="gridStatsChip" class="font14 bold">
                                <span class="whiteText">{{ stats.totalBetCount }}</span>
                            </v-chip>

                            <span
                                v-if="display.width.value > 810"
                                :data-tooltip="{ text: `${t('bet.validTurnover')}: ${userStore.currency.symbol}${formatNumber(stats.totalValidStakeSum, 'money')}` }"
                                class="stats-label pl-5 pr-1"
                                :class="display.width.value <= 667 ? 'font12' : 'font14'"
                            >
                                {{ t('bet.stake') }}
                            </span>
                            <v-chip v-if="display.width.value > 810" variant="flat" color="gridStatsChip" class="font14 bold">
                                <span class="whiteText">{{ userStore.currency.symbol }}{{ formatNumber(stats.totalStakeSum, 'money') }}</span>
                            </v-chip>

                            <span v-if="display.width.value > 810" class="stats-label pl-5 pr-1" :class="display.width.value <= 667 ? 'font12' : 'font14'">{{ t('bet.currentPosition') }}</span>
                            <v-chip v-if="display.width.value > 810" variant="flat" :color="position >= 0 ? 'gridStatsPositionGreenChip' : 'gridStatsPositionRedChip'" class="font14 bold">
                                <span :class="position >= 0 ? (theme.name.value === 'dark' ? 'tbgreen-text' : 'whiteText') : 'text-red'">
                                    {{ userStore.currency.symbol }}{{ formatNumber(position, 'money') }}
                                </span>
                            </v-chip>
                        </span>
                        <v-btn tag="a" @click="cancel" icon variant="text" size="small" density="default">
                            <v-icon size="24" :icon="mdiClose" />
                        </v-btn>
                    </v-row>

                    <v-row v-if="betStore.items.length > 0 && display.width.value <= 810" class="pa-0 ma-0 d-flex align-center justify-start">
                        <span class="stats-label pr-1 font12">{{ t('bet.bets') }}</span>
                        <v-chip variant="flat" color="gridStatsChip" size="small" class="font14 bold">
                            <span class="whiteText">{{ stats.totalBetCount }}</span>
                        </v-chip>

                        <span class="stats-label pl-1 pr-1 font12">{{ t('bet.stake') }}</span>
                        <v-chip variant="flat" color="gridStatsChip" size="small" class="font14 bold">
                            <span class="whiteText">{{ userStore.currency.symbol }}{{ formatNumber(stats.totalStakeSum, 'money') }}</span>
                        </v-chip>

                        <span class="stats-label pl-1 pr-1 font12">{{ t('bet.currentPosition') }}</span>
                        <v-chip variant="flat" size="small" :color="position >= 0 ? 'gridStatsPositionGreenChip' : 'gridStatsPositionRedChip'" class="font14 bold">
                            <span :class="position >= 0 ? (theme.name.value === 'dark' ? 'tbgreen-text' : 'whiteText') : 'text-red'">{{ userStore.currency.symbol }}{{ position.toFixed(2) }}</span>
                        </v-chip>
                    </v-row>
                </v-container>
            </v-card-title>
        </template>
        <template v-slot:content>
            <v-card-text :class="display.width.value <= 667 ? 'pr-3 pl-0 pt-0 pb-2' : 'pb-0 pt-0'">
                <v-container class="pa-0" fluid v-if="gameGridItem" :class="display.height.value > 700 ? 'min-h-700' : ''">
                    <v-col v-if="loading" class="text-center">
                        <v-progress-circular indeterminate color="green"></v-progress-circular>
                    </v-col>
                    <v-col v-else class="pa-0">
                        <v-row class="my-0">
                            <v-col class="px-0" :class="display.width.value <= 667 ? 'pl-4' : ''">
                                <img class="mb3 mr-1" v-if="gameGridItem.iso3" :alt="gameGridItem.iso3" :src="getCountryFlagUrl(gameGridItem.iso3)" width="15px" height="auto" />
                                <img class="mb3 mr-1" v-else alt="world" :src="getCountryFlagUrl('world')" width="15" height="auto" />

                                <span class="font14 text-left" :class="theme.name.value === 'dark' ? 'whiteText' : 'text-black'">{{ gameGridItem.leagueName }}</span>
                            </v-col>
                        </v-row>

                        <v-row class="my-0">
                            <v-divider></v-divider>
                        </v-row>

                        <v-row class="my-0">
                            <v-tabs v-model="tab" background-color="transparent" grow height="48">
                                <v-tab :disabled="listDisabled" class="font14" height="48" :color="theme.name.value === 'dark' ? 'grey' : ''">
                                    <v-icon color="grey" :size="15" class="mr-2" icon="$soccerBall" />
                                    {{ t('gameGrid.regularTime') }}
                                </v-tab>
                                <v-tab :disabled="listCornersDisabled" class="font14" height="48" :color="theme.name.value === 'dark' ? 'grey' : ''">
                                    <v-icon color="grey" :size="14" class="mr-2" icon="$cornerFlag" />
                                    {{ t('gameGrid.regularTimeCorners') }}
                                </v-tab>
                            </v-tabs>
                        </v-row>

                        <v-tabs-window v-model="tab">
                            <v-tabs-window-item>
                                <v-container class="pa-0" fluid>
                                    <v-row class="my-0">
                                        <v-col
                                            class="text-center bold d-flex justify-center align-center"
                                            :class="[display.width.value <= 667 ? 'py-2' : '', display.width.value <= 667 ? 'h-42' : 'h-52']"
                                        >
                                            <img v-if="gameGridItem.teamAwayLogo" :src="getTeamLogoUrl(gameGridItem.teamAwayLogo)" class="logo" alt="team" />
                                            <img v-else src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="logo" alt="team" />
                                            <span class="pl-2" :class="[gameGridItem.jollywhere === Jollywhere.Away ? 'jollywhereTeam' : 'dogTeam', display.width.value <= 667 ? 'font20' : 'font20']">
                                                {{ gameGridItem.teamAwayName }}
                                                <v-chip v-if="gameGridItem.eventFeedType === EventFeedType.Live" color="orange" variant="flat" label class="mb-1">
                                                    <span class="score bold">{{ gameGridItem.scoreAway }}</span>
                                                </v-chip>
                                            </span>
                                        </v-col>
                                    </v-row>

                                    <v-row class="my-0">
                                        <v-col class="vert-text d-flex justify-center align-center" :class="[display.width.value <= 667 ? 'px-1' : '', display.width.value <= 667 ? 'w-48' : 'w-52']">
                                            <img v-if="gameGridItem.teamHomeLogo" :src="getTeamLogoUrl(gameGridItem.teamHomeLogo)" class="logo-rotate" alt="team" />
                                            <img v-else src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="logo-rotate" alt="team" />
                                            <span
                                                class="px-0 bold d-flex justify-center align-center pt-2"
                                                :class="[gameGridItem.jollywhere === Jollywhere.Home ? 'jollywhereTeam' : 'dogTeam', display.width.value <= 667 ? 'font20' : 'font20']"
                                            >
                                                {{ gameGridItem.teamHomeName }}
                                                <v-chip v-if="gameGridItem.eventFeedType === EventFeedType.Live" color="orange" variant="flat" label class="chip-25x32 mt-2">
                                                    <span class="score bold">{{ gameGridItem.scoreHome }}</span>
                                                </v-chip>
                                            </span>
                                        </v-col>

                                        <v-col :cols="display.width.value <= 720 ? 10 : 11" class="pa-0" :class="theme.name.value === 'dark' ? 'border-dark' : 'border-light'">
                                            <v-data-table-server
                                                :headers="headersBetGrid"
                                                :items="listBetsGrid as any[]"
                                                :items-length="listBetsGrid.length"
                                                item-value="id"
                                                :mobile-breakpoint="0"
                                                disable-sort
                                                disable-pagination
                                                density="compact"
                                                hide-default-footer
                                                class="goalsGrid w-100"
                                            >
                                                <template v-slot:headers="{ columns }">
                                                    <tr>
                                                        <th
                                                            v-for="head in columns"
                                                            class="v-data-table__td v-data-table-column--align-center v-data-table__th"
                                                            colspan="1"
                                                            rowspan="1"
                                                            aria-label="Goals"
                                                            :class="headClass(head)"
                                                        >
                                                            <div class="v-data-table-header__content minw-60">
                                                                <v-icon v-if="head.title === ''" color="grey" :size="15" icon="$soccerBall" />
                                                                <span
                                                                    v-else
                                                                    class="font13"
                                                                    :class="
                                                                        Number(head.title) === Number(gameGridItem.scoreAway)
                                                                            ? theme.name.value === 'dark'
                                                                                ? 'whiteText bold'
                                                                                : 'text-black bold'
                                                                            : ''
                                                                    "
                                                                >
                                                                    {{ head.title }}
                                                                </span>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </template>

                                                <template v-slot:item="i">
                                                    <tr>
                                                        <td
                                                            :class="[
                                                                goalClass(i.item.goal),
                                                                Number(i.item.goal) === Number(gameGridItem.scoreHome)
                                                                    ? theme.name.value === 'dark'
                                                                        ? 'whiteText bold'
                                                                        : 'blackText bold'
                                                                    : 'grey--text',
                                                            ]"
                                                        >
                                                            {{ i.item.goal }}
                                                        </td>
                                                        <td :class="i.item.goal + '-0' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_0 > 0">+{{ formatNumber(i.item.balance_0, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_0 < 0">{{ formatNumber(i.item.balance_0, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_0 == 0">
                                                                {{ formatNumber(i.item.balance_0, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-1' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_1 > 0">+{{ formatNumber(i.item.balance_1, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_1 < 0">{{ formatNumber(i.item.balance_1, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_1 == 0">
                                                                {{ formatNumber(i.item.balance_1, 'money') }}
                                                            </span>
                                                        </td>

                                                        <td :class="i.item.goal + '-2' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_2 > 0">+{{ formatNumber(i.item.balance_2, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_2 < 0">{{ formatNumber(i.item.balance_2, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_2 == 0">
                                                                {{ formatNumber(i.item.balance_2, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-3' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_3 > 0">+{{ formatNumber(i.item.balance_3, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_3 < 0">{{ formatNumber(i.item.balance_3, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_3 == 0">
                                                                {{ formatNumber(i.item.balance_3, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-4' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_4 > 0">+{{ formatNumber(i.item.balance_4, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_4 < 0">{{ formatNumber(i.item.balance_4, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_4 == 0">
                                                                {{ formatNumber(i.item.balance_4, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-5' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_5 > 0">+{{ formatNumber(i.item.balance_5, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_5 < 0">{{ formatNumber(i.item.balance_5, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_5 == 0">
                                                                {{ formatNumber(i.item.balance_5, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-6' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_6 > 0">+{{ formatNumber(i.item.balance_6, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_6 < 0">{{ formatNumber(i.item.balance_6, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_6 == 0">
                                                                {{ formatNumber(i.item.balance_6, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-7' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_7 > 0">+{{ formatNumber(i.item.balance_7, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_7 < 0">{{ formatNumber(i.item.balance_7, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_7 == 0">
                                                                {{ formatNumber(i.item.balance_7, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-8' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_8 > 0">+{{ formatNumber(i.item.balance_8, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_8 < 0">{{ formatNumber(i.item.balance_8, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_8 == 0">
                                                                {{ formatNumber(i.item.balance_8, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-9' === gameGridItem.scoreFull ? 'yellowBg bold text-center br no-padding-x' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_9 > 0">+{{ formatNumber(i.item.balance_9, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_9 < 0">{{ formatNumber(i.item.balance_9, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_9 == 0">
                                                                {{ formatNumber(i.item.balance_9, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item.goal + '-10' === gameGridItem.scoreFull ? 'yellowBg bold text-center' : 'text-center'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_10 > 0">+{{ formatNumber(i.item.balance_10, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_10 < 0">
                                                                {{ formatNumber(i.item.balance_10, 'money') }}
                                                            </span>
                                                            <span class="yellowText" v-else-if="i.item.balance_10 == 0">
                                                                {{ formatNumber(i.item.balance_10, 'money') }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </v-data-table-server>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-tabs-window-item>
                            <v-tabs-window-item>
                                <v-container class="pa-0" fluid>
                                    <v-row class="my-0">
                                        <v-col
                                            class="text-center bold d-flex justify-center align-center"
                                            :class="[display.width.value <= 667 ? 'py-2' : '', display.width.value <= 667 ? 'h-42' : 'h-52']"
                                        >
                                            <img v-if="gameGridItem.teamAwayLogo" :src="getTeamLogoUrl(gameGridItem.teamAwayLogo)" class="logo" alt="team" />
                                            <img v-else src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="logo" alt="team" />
                                            <span class="pl-2" :class="[gameGridItem.jollywhere === Jollywhere.Away ? 'jollywhereTeam' : 'dogTeam', display.width.value <= 667 ? 'font20' : 'font20']">
                                                {{ gameGridItem.teamAwayName }}
                                                <v-chip v-if="gameGridItem.eventFeedType === EventFeedType.Live" color="orange" variant="flat" label class="mb-1">
                                                    <span class="score bold">{{ gameGridItem.cornersAway }}</span>
                                                </v-chip>
                                            </span>
                                        </v-col>
                                    </v-row>

                                    <v-row class="my-0">
                                        <v-col class="vert-text d-flex justify-center align-center" :class="[display.width.value <= 667 ? 'px-1' : '', display.width.value <= 667 ? 'w-48' : 'w-52']">
                                            <img v-if="gameGridItem.teamHomeLogo" :src="getTeamLogoUrl(gameGridItem.teamHomeLogo)" class="logo-rotate" alt="team" />
                                            <img v-else src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="logo-rotate" alt="team" />
                                            <span
                                                class="px-0 bold d-flex justify-center align-center pt-2"
                                                :class="[gameGridItem.jollywhere === Jollywhere.Home ? 'jollywhereTeam' : 'dogTeam', display.width.value <= 667 ? 'font20' : 'font20']"
                                            >
                                                {{ gameGridItem.teamHomeName }}
                                                <v-chip v-if="gameGridItem.eventFeedType === EventFeedType.Live" color="orange" variant="flat" label class="chip-25x32 mt-2">
                                                    <span class="score bold">{{ gameGridItem.cornersHome }}</span>
                                                </v-chip>
                                            </span>
                                        </v-col>

                                        <v-col :cols="display.width.value <= 667 ? 9 : 11" class="pa-0" :class="theme.name.value === 'dark' ? 'border-dark' : 'border-light'">
                                            <v-data-table-server
                                                :headers="headersCorners"
                                                :items="listCornersGrid as any[]"
                                                :items-length="listCornersGrid.length"
                                                item-value="id"
                                                :mobile-breakpoint="0"
                                                disable-sort
                                                disable-pagination
                                                density="compact"
                                                hide-default-footer
                                                class="goalsGrid w-100"
                                            >
                                                <template v-slot:headers="{ columns }">
                                                    <tr>
                                                        <th
                                                            v-for="head in columns"
                                                            class="v-data-table__td v-data-table-column--align-center v-data-table__th"
                                                            colspan="1"
                                                            rowspan="1"
                                                            aria-label="Corners"
                                                            :class="cornersHeadClass(head)"
                                                        >
                                                            <div class="v-data-table-header__content minw-60">
                                                                <v-icon v-if="head.title === ''" color="grey" :size="14" icon="$cornerFlag" />
                                                                <span
                                                                    v-else
                                                                    class="font13"
                                                                    :class="
                                                                        Number(cornersHeaders(head)) === Number(gameGridItem.cornersAway)
                                                                            ? theme.name.value === 'dark'
                                                                                ? 'whiteText bold'
                                                                                : 'blackText bold'
                                                                            : ''
                                                                    "
                                                                >
                                                                    {{ cornersHeaders(head) }}
                                                                </span>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </template>

                                                <template v-slot:item="i">
                                                    <tr>
                                                        <td
                                                            :class="[
                                                                cornersClass(i.item.cornersHome),
                                                                Number(i.item.cornersHome) === Number(gameGridItem.cornersHome)
                                                                    ? theme.name.value === 'dark'
                                                                        ? 'whiteText bold'
                                                                        : 'blackText bold'
                                                                    : 'grey--text',
                                                            ]"
                                                        >
                                                            {{ i.item.cornersHome }}
                                                        </td>
                                                        <td :class="i.item[0].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_0 > 0">+{{ formatNumber(i.item.balance_0, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_0 < 0">{{ formatNumber(i.item.balance_0, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_0 == 0">
                                                                {{ formatNumber(i.item.balance_0, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[1].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_1 > 0">+{{ formatNumber(i.item.balance_1, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_1 < 0">{{ formatNumber(i.item.balance_1, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_1 == 0">
                                                                {{ formatNumber(i.item.balance_1, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[2].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_2 > 0">+{{ formatNumber(i.item.balance_2, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_2 < 0">{{ formatNumber(i.item.balance_2, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_2 == 0">
                                                                {{ formatNumber(i.item.balance_2, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[3].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_3 > 0">+{{ formatNumber(i.item.balance_3, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_3 < 0">{{ formatNumber(i.item.balance_3, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_3 == 0">
                                                                {{ formatNumber(i.item.balance_3, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[4].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_4 > 0">+{{ formatNumber(i.item.balance_4, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_4 < 0">{{ formatNumber(i.item.balance_4, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_4 == 0">
                                                                {{ formatNumber(i.item.balance_4, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[5].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_5 > 0">+{{ formatNumber(i.item.balance_5, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_5 < 0">{{ formatNumber(i.item.balance_5, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_5 == 0">
                                                                {{ formatNumber(i.item.balance_5, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[6].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_6 > 0">+{{ formatNumber(i.item.balance_6, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_6 < 0">{{ formatNumber(i.item.balance_6, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_6 == 0">
                                                                {{ formatNumber(i.item.balance_6, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[7].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_7 > 0">+{{ formatNumber(i.item.balance_7, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_7 < 0">{{ formatNumber(i.item.balance_7, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_7 == 0">
                                                                {{ formatNumber(i.item.balance_7, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[8].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_8 > 0">+{{ formatNumber(i.item.balance_8, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_8 < 0">{{ formatNumber(i.item.balance_8, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_8 == 0">
                                                                {{ formatNumber(i.item.balance_8, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[9].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center br' : 'text-center br'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_9 > 0">+{{ formatNumber(i.item.balance_9, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_9 < 0">{{ formatNumber(i.item.balance_9, 'money') }}</span>
                                                            <span class="yellowText" v-else-if="i.item.balance_9 == 0">
                                                                {{ formatNumber(i.item.balance_9, 'money') }}
                                                            </span>
                                                        </td>
                                                        <td :class="i.item[10].cornersSum === gameGridItem.cornersSum ? 'yellowBg bold text-center' : 'text-center'">
                                                            <span v-if="i.item.statusId === BetStatus.Rejected">0.00</span>
                                                            <span class="greenText" v-else-if="i.item.balance_10 > 0">+{{ formatNumber(i.item.balance_10, 'money') }}</span>
                                                            <span class="redText" v-else-if="i.item.balance_10 < 0">
                                                                {{ formatNumber(i.item.balance_10, 'money') }}
                                                            </span>
                                                            <span class="yellowText" v-else-if="i.item.balance_10 == 0">
                                                                {{ formatNumber(i.item.balance_10, 'money') }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </v-data-table-server>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-tabs-window-item>
                        </v-tabs-window>

                        <v-row class="my-0 pt-6 pb-3">
                            <v-divider></v-divider>
                        </v-row>

                        <v-row class="my-0 overflow-hidden">
                            <v-data-table-server
                                :headers="headersBets"
                                :items="listBets as BetItem[]"
                                :items-length="listBets.length"
                                item-value="id"
                                :mobile-breakpoint="0"
                                disable-sort
                                disable-pagination
                                hide-default-footer
                                class="gameGrid w-100 radius-0"
                                :class="theme.name.value === 'dark' ? 'bb-dark' : 'bb-light'"
                            >
                                <template #item.createdAt="{ item }: { item: BetItem }">
                                    <span :data-tooltip="dateFormatInUserTimeZone(item.createdAt, 'dd-MM-yyyy HH:mm:ss')">{{ dateFormatInUserTimeZone(item.createdAt, 'HH:mm:ss') }}</span>
                                </template>

                                <template #item.botName="{ item }: { item: BetItem }">
                                    <span v-if="item.orderId">
                                        <v-icon :data-tooltip="t('bet.orderIcon')" class="pb-1" size="16" :color="theme.name.value === 'dark' ? '' : '#867878'" :icon="mdiClipboardClockOutline" />
                                        #{{ item.orderId }}
                                    </span>

                                    <span v-else-if="item.botDeletedAt">
                                        <v-icon :data-tooltip="item.botName" class="pb-1" size="16" :color="theme.name.value === 'dark' ? '' : '#867878'" :icon="mdiRobotOutline" />
                                        <span class="redText">{{ t('bet.deleted') }}</span>
                                    </span>

                                    <span v-else>
                                        <v-icon :data-tooltip="t('bet.botIcon')" class="pb-1" size="16" :color="theme.name.value === 'dark' ? '' : '#867878'" :icon="mdiRobotOutline" />
                                        {{ item.botName }}
                                    </span>
                                </template>

                                <template #item.bookmakerId="{ item }: { item: BetItem }">{{ item.getBookmaker().name }}</template>

                                <template #item.statusId="{ item }: { item: BetItem }">
                                    <span :class="'betStatus' + item.statusId">{{ item.statusName }}</span>
                                </template>

                                <template #item.type="{ item }: { item: BetItem }">{{ item.getSelectionName(true) }}</template>

                                <template #item.odds="{ item }: { item: BetItem }">@{{ formatOdds(item.odds) }}</template>

                                <template #item.score="{ item }: { item: BetItem }">
                                    <v-row class="pa-0 ma-0">
                                        <v-col class="pa-0 ma-0 w-22-fixed" v-if="item.isLive">
                                            <v-icon v-if="item.marketGroup === MarketGroup.Corners" style="margin-bottom: 2px" color="grey" :size="14" icon="$cornerFlag" />
                                            <v-icon v-else :size="15" style="margin-bottom: 2px" color="grey" icon="$soccerBall" />
                                        </v-col>

                                        <v-col v-if="item.isLive" class="pa-0 ma-0 d-flex text-center flex-column align-start justify-center">
                                            <span v-if="item.displayedScore">{{ item.displayedScore }}</span>
                                            <span v-else :data-tooltip="t(item.marketGroup === MarketGroup.Corners ? 'bet.cornersNotConfirmed' : 'bet.scoreNotConfirmed')">{{ t('bet.NC') }}</span>
                                        </v-col>
                                        <v-col v-else class="pa-0 ma-0">&nbsp;</v-col>
                                    </v-row>
                                </template>

                                <template #item.minute="{ item }: { item: BetItem }">
                                    <span v-if="item.isLive">
                                        <span v-if="item.time">{{ item.displayedMinute }}</span>
                                        <span v-else :data-tooltip="t('bet.timeNotConfirmed')">{{ t('bet.NC') }}</span>
                                    </span>
                                    <span v-else :data-tooltip="t('bet.pregame')">{{ t('bet.pg') }}</span>
                                </template>

                                <template #item.stake="{ item }: { item: BetItem }">{{ formatNumber(Number(item.stake), 'money') }}</template>

                                <template #item.expected="{ item }">
                                    <span :class="item.balance !== null ? 'bold' : ''" v-html="expected(item)"></span>
                                </template>
                            </v-data-table-server>
                        </v-row>
                    </v-col>
                </v-container>
            </v-card-text>
        </template>
    </FormDialog>
</template>
<style scoped src="./style.css"></style>
